import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Toggle } from "../Toggle";
import { Button } from "../Button";
import {
  analyticsAllowed,
  useAnalyticsConsent,
} from "../../hooks/useAnalyticsConsent";

/**
 * Cookie preferences dialog. Essential is always on (informational); Analytics
 * is opt-out — it starts allowed and the visitor can turn it off. Reuses the
 * shared Toggle + Button primitives and mirrors ConfirmDialog's overlay.
 */
export function CookiePreferencesModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { optIn, optOut } = useAnalyticsConsent();
  const [analytics, setAnalytics] = useState(true);

  // Reflect the stored choice each time the dialog opens.
  useEffect(() => {
    if (open) setAnalytics(analyticsAllowed());
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const persist = (allow: boolean) => {
    if (allow) optIn();
    else optOut();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("landing.cookiePrefs.title")}
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-slate-200/80 dark:bg-slate-800 dark:ring-slate-700/80"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold text-fg-primary">
          {t("landing.cookiePrefs.title")}
        </h2>
        <p className="mt-2 text-sm text-fg-secondary">
          {t("landing.cookiePrefs.intro")}
        </p>

        <div className="mt-5 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-fg-primary">
                {t("landing.cookiePrefs.essentialLabel")}
              </p>
              <p className="mt-0.5 text-xs text-fg-secondary">
                {t("landing.cookiePrefs.essentialDesc")}
              </p>
            </div>
            <Toggle
              checked
              disabled
              onChange={() => {}}
              aria-label={t("landing.cookiePrefs.essentialLabel")}
            />
          </div>

          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-fg-primary">
                {t("landing.cookiePrefs.analyticsLabel")}
              </p>
              <p className="mt-0.5 text-xs text-fg-secondary">
                {t("landing.cookiePrefs.analyticsDesc")}
              </p>
            </div>
            <Toggle
              checked={analytics}
              onChange={setAnalytics}
              aria-label={t("landing.cookiePrefs.analyticsLabel")}
            />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => persist(false)}
            className="text-sm font-medium text-fg-secondary hover:text-fg-primary"
          >
            {t("landing.cookiePrefs.essentialOnly")}
          </button>
          <Button variant="secondary" size="md" onClick={() => persist(true)}>
            {t("landing.cookiePrefs.acceptAll")}
          </Button>
          <Button size="md" onClick={() => persist(analytics)}>
            {t("landing.cookiePrefs.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}
